import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMember } from '@/lib/api'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

export default function MemberDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()
  const { confirm, dialog } = useConfirm()

  const [member, setMember] = useState<AdminMember | null>(
    (location.state as { member?: AdminMember } | null)?.member ?? null
  )
  const [loading, setLoading] = useState(!member)

  useEffect(() => {
    if (member || !token || !id) return
    api.getAdminMembers(token)
      .then((items) => setMember(items.find((m) => m.id === id) ?? null))
      .finally(() => setLoading(false))
  }, [member, id, token])

  const handleDelete = async () => {
    if (!member) return
    if (!await confirm(
      `"${member.stage_name}" akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${member.stage_name}"` }
    )) return
    if (!token) return
    try {
      await api.deleteMember(token, member.id)
      navigate(-1)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (!member) {
    return <div className="p-6"><p className="text-red-400 text-sm">Member not found.</p></div>
  }

  return (
    <div className="p-6">
      {/* Nav */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft size={14} />
          Kembali
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/members/${member.id}/edit`, { state: { member } })}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded hover:bg-zinc-800"
          >
            Hapus
          </button>
        </div>
      </div>

      <div className="max-w-xl mx-auto">
      {/* Hero card */}
      <div className="flex bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden mb-4">
        {/* Portrait photo */}
        <div className="w-36 flex-shrink-0 bg-zinc-800 relative">
          {member.image_url ? (
            <img
              src={normalizeImageKitUrl(member.image_url)}
              alt={member.stage_name}
              className="w-full h-full object-cover object-top"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className="w-full h-full min-h-[144px] flex items-center justify-center">
              <span className="text-zinc-600 text-4xl font-bold">{member.stage_name[0]}</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-white leading-tight truncate">
                  {member.stage_name}
                </h1>
                {member.real_name && (
                  <p className="text-zinc-400 text-sm mt-0.5 truncate">{member.real_name}</p>
                )}
              </div>
              <span className={`text-[10px] font-medium px-2 py-1 rounded-full flex-shrink-0 ${
                member.is_active
                  ? 'bg-green-500/15 text-green-400 border border-green-500/20'
                  : 'bg-zinc-800 text-zinc-500'
              }`}>
                {member.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(`/groups?slug=${member.group_slug}`)}
                className="text-[11px] text-zinc-300 bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1 rounded transition-colors"
              >
                {member.group_slug}
              </button>
              <span className="text-[11px] text-zinc-500 bg-zinc-800/50 px-2.5 py-1 rounded">
                {member.role}
              </span>
            </div>
          </div>

          <div className="mt-4 space-y-1.5">
            {member.debut_date && (
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-zinc-600 w-16 flex-shrink-0">Debut</span>
                <span className="text-xs text-zinc-300">{member.debut_date}</span>
              </div>
            )}
            {member.birth_year && (
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-zinc-600 w-16 flex-shrink-0">Lahir</span>
                <span className="text-xs text-zinc-300">{member.birth_year}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Positions */}
        {member.positions.length > 0 && (
          <Section title="Posisi">
            <div className="flex flex-wrap gap-2">
              {member.positions.map((pos) => (
                <span
                  key={pos}
                  className="text-xs text-zinc-300 bg-zinc-800 px-3 py-1.5 rounded-lg"
                >
                  {pos}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Meta */}
        <Section title="Info Teknis">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Member ID</p>
              <p className="text-xs text-zinc-400 font-mono break-all">{member.id}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Group ID</p>
              <p className="text-xs text-zinc-600 font-mono break-all">{member.group_id}</p>
            </div>
          </div>
        </Section>
      </div>

      </div>
      {dialog}
    </div>
  )
}
